import { Vector } from "../Core/Utils/Vector";
export function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}
export function mix(comp1, comp2, weight1, weight2) {
  return Math.floor((comp1 * weight1 + comp2 * weight2) / (weight1 + weight2));
}
export function randomInRange(r) {
  var max = getRangeMax(r);
  var min = getRangeMin(r);
  if (max === min) {
    min = 0;
  }
  return Math.random() * (max - min) + min;
}
export function getRangeValue(value) {
  return typeof value === "number" ? value : randomInRange(value);
}
export function getRangeMin(value) {
  return typeof value === "number" ? value : value.min;
}
export function getRangeMax(value) {
  return typeof value === "number" ? value : value.max;
}
export function setRangeValue(source, value) {
  if (source === value || value === undefined && typeof source === "number") {
    return source;
  }
  var min = getRangeMin(source),
    max = getRangeMax(source);
  return value !== undefined ? {
    min: Math.min(min, value),
    max: Math.max(max, value)
  } : setRangeValue(min, max);
}
export function getValue(options) {
  var random = options.random,
    _ref = typeof random === "boolean" ? {
      enable: random,
      minimumValue: 0
    } : random,
    enable = _ref.enable,
    minimumValue = _ref.minimumValue;
  return enable ? getRangeValue(setRangeValue(options.value, minimumValue)) : getRangeValue(options.value);
}
export function getDistances(pointA, pointB) {
  var dx = pointA.x - pointB.x,
    dy = pointA.y - pointB.y;
  return {
    dx: dx,
    dy: dy,
    distance: Math.sqrt(dx * dx + dy * dy)
  };
}
export function getDistance(pointA, pointB) {
  return getDistances(pointA, pointB).distance;
}
export function getParticleDirectionAngle(direction) {
  if (typeof direction === "number") {
    return direction * Math.PI / 180;
  } else {
    switch (direction) {
      case "top":
        return -Math.PI / 2;
      case "top-right":
        return -Math.PI / 4;
      case "right":
        return 0;
      case "bottom-right":
        return Math.PI / 4;
      case "bottom":
        return Math.PI / 2;
      case "bottom-left":
        return 3 * Math.PI / 4;
      case "left":
        return Math.PI;
      case "top-left":
        return -3 * Math.PI / 4;
      case "none":
      default:
        return Math.random() * Math.PI * 2;
    }
  }
}
export function getParticleBaseVelocity(direction) {
  var baseVelocity = Vector.origin;
  baseVelocity.length = 1;
  baseVelocity.angle = direction;
  return baseVelocity;
}
export function collisionVelocity(v1, v2, m1, m2) {
  return Vector.create(v1.x * (m1 - m2) / (m1 + m2) + v2.x * 2 * m2 / (m1 + m2), v1.y);
}
export function calcEasing(value, type) {
  switch (type) {
    case "ease-out-quad":
      return 1 - Math.pow(1 - value, 2);
    case "ease-out-cubic":
      return 1 - Math.pow(1 - value, 3);
    case "ease-out-quart":
      return 1 - Math.pow(1 - value, 4);
    case "ease-out-quint":
      return 1 - Math.pow(1 - value, 5);
    case "ease-out-expo":
      return value === 1 ? 1 : 1 - Math.pow(2, -10 * value);
    case "ease-out-sine":
      return Math.sin(value * Math.PI / 2);
    case "ease-out-back":
      {
        var c1 = 1.70158,
          c3 = c1 + 1;
        return 1 + c3 * Math.pow(value - 1, 3) + c1 * Math.pow(value - 1, 2);
      }
    case "ease-out-circ":
      return Math.sqrt(1 - Math.pow(value - 1, 2));
    default:
      return value;
  }
}
export function calcPositionFromSize(data) {
  var _a, _b;
  return ((_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== undefined && ((_b = data.position) === null || _b === void 0 ? void 0 : _b.y) !== undefined ? {
    x: data.position.x * data.size.width / 100,
    y: data.position.y * data.size.height / 100
  } : undefined;
}
export function calcPositionOrRandomFromSize(data) {
  var _a, _b, _c, _d;
  return {
    x: ((_b = (_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : Math.random() * 100) * data.size.width / 100,
    y: ((_d = (_c = data.position) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : Math.random() * 100) * data.size.height / 100
  };
}
export function calcPositionOrRandomFromSizeRanged(data) {
  var _a, _b;
  var position = {
    x: ((_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== undefined ? getRangeValue(data.position.x) : undefined,
    y: ((_b = data.position) === null || _b === void 0 ? void 0 : _b.y) !== undefined ? getRangeValue(data.position.y) : undefined
  };
  return calcPositionOrRandomFromSize({
    size: data.size,
    position: position
  });
}
export function calcExactPositionOrRandomFromSize(data) {
  var _a, _b, _c, _d;
  return {
    x: (_b = (_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : Math.random() * data.size.width,
    y: (_d = (_c = data.position) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : Math.random() * data.size.height
  };
}
export function calcExactPositionOrRandomFromSizeRanged(data) {
  var _a, _b;
  var position = {
    x: ((_a = data.position) === null || _a === void 0 ? void 0 : _a.x) !== undefined ? getRangeValue(data.position.x) : undefined,
    y: ((_b = data.position) === null || _b === void 0 ? void 0 : _b.y) !== undefined ? getRangeValue(data.position.y) : undefined
  };
  return calcExactPositionOrRandomFromSize({
    size: data.size,
    position: position
  });
}